import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Cubo } from '../tipos';

/**
 * El plan de un artículo en el tiempo.
 *
 * Un solo eje, a propósito: todo lo que hay aquí son unidades del mismo
 * artículo. Poner el stock en un eje y los movimientos en otro dejaría
 * comparar visualmente dos escalas distintas, que es la forma más fácil de
 * hacer que un gráfico mienta sin decir nada falso.
 *
 *   Necesidades   barra hacia abajo   (lo que sale)
 *   Cobertura     barra hacia arriba  (lo que entra y ya existe)
 *   Propuestas    barra hacia arriba con trama (lo que entraría SI se hace)
 *   Proyectado    línea continua      (cómo queda el stock con el plan)
 *   Sin hacer nada línea discontinua   (cómo quedaría si nadie hace nada)
 *
 * La trama de las propuestas no es decoración: es lo que distingue «esto está
 * pedido» de «esto habría que pedirlo», y lo hace sin depender del color.
 */

const ALTO = 280;
const MARGEN = { arriba: 16, derecha: 64, abajo: 34, izquierda: 56 };

interface Props {
  buckets: Cubo[];
  safetyStock: number;
  unidad: string | null;
}

export function GraficoPlan({ buckets, safetyStock, unidad }: Props): JSX.Element {
  const id = useId();
  const contenedor = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(880);
  const [encima, setEncima] = useState<number | null>(null);
  const [raton, setRaton] = useState({ x: 0, y: 0 });

  // Se mide el contenedor en vez de fijar el ancho: el gráfico tiene que
  // servir igual en una pantalla de taller de 4K y en un portátil.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const el = contenedor.current;
    if (!el) return;
    const ro = new ResizeObserver((entradas) => {
      const w = entradas[0]?.contentRect.width;
      if (w && w > 200) setAncho(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const escala = useMemo(() => {
    const valores = buckets.flatMap((b) => [b.projected, b.projectedBare, b.supply + b.planned, -b.demand, safetyStock, 0]);
    const max = Math.max(...valores, 1);
    const min = Math.min(...valores, 0);
    const margen = (max - min) * 0.08 || 1;
    return { max: max + margen, min: min - margen };
  }, [buckets, safetyStock]);

  const anchoUtil = Math.max(ancho - MARGEN.izquierda - MARGEN.derecha, 120);
  const altoUtil = ALTO - MARGEN.arriba - MARGEN.abajo;
  const paso = anchoUtil / Math.max(buckets.length, 1);

  const y = (v: number): number =>
    MARGEN.arriba + ((escala.max - v) / (escala.max - escala.min)) * altoUtil;
  const xCentro = (i: number): number => MARGEN.izquierda + paso * (i + 0.5);

  const cero = y(0);
  const anchoBarra = Math.max(Math.min(paso * 0.28, 20), 3);

  const lineaProyectado = buckets.map((b, i) => `${i === 0 ? 'M' : 'L'}${xCentro(i)},${y(b.projected)}`).join(' ');
  const lineaSinPlan = buckets.map((b, i) => `${i === 0 ? 'M' : 'L'}${xCentro(i)},${y(b.projectedBare)}`).join(' ');

  const marcas = marcasEje(escala.min, escala.max);
  const ultimo = buckets[buckets.length - 1];

  return (
    <div ref={contenedor} style={{ position: 'relative' }}>
      <svg
        className="grafico"
        viewBox={`0 0 ${ancho} ${ALTO}`}
        height={ALTO}
        role="img"
        aria-label="Necesidades, coberturas y stock proyectado por periodo"
        onMouseLeave={() => setEncima(null)}
        onMouseMove={(e) => {
          const caja = e.currentTarget.getBoundingClientRect();
          const escalaX = ancho / caja.width;
          const px = (e.clientX - caja.left) * escalaX;
          const i = Math.floor((px - MARGEN.izquierda) / paso);
          setEncima(i >= 0 && i < buckets.length ? i : null);
          setRaton({ x: e.clientX, y: e.clientY });
        }}
      >
        <defs>
          {/* Trama a 45°: el canal que no depende del color. */}
          <pattern id={`trama-${id}`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="var(--superficie)" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="var(--serie-3)" strokeWidth="3.5" />
          </pattern>
        </defs>

        {/* Rejilla y eje: recesivos, nunca compiten con los datos. */}
        {marcas.map((m) => (
          <g key={m}>
            <line x1={MARGEN.izquierda} x2={ancho - MARGEN.derecha} y1={y(m)} y2={y(m)} stroke="var(--linea)" strokeWidth="1" />
            <text x={MARGEN.izquierda - 8} y={y(m) + 4} textAnchor="end" fontSize="11" fill="var(--tinta-3)">
              {formatear(m)}
            </text>
          </g>
        ))}
        <line x1={MARGEN.izquierda} x2={ancho - MARGEN.derecha} y1={cero} y2={cero} stroke="var(--eje)" strokeWidth="1" />

        {/* Stock de seguridad: una referencia, no una serie. */}
        {safetyStock > 0 && (
          <g>
            <line
              x1={MARGEN.izquierda}
              x2={ancho - MARGEN.derecha}
              y1={y(safetyStock)}
              y2={y(safetyStock)}
              stroke="var(--aviso)"
              strokeWidth="1.5"
              strokeDasharray="2 4"
            />
            <text x={ancho - MARGEN.derecha + 6} y={y(safetyStock) + 4} fontSize="11" fill="var(--tinta-2)">
              mínimo
            </text>
          </g>
        )}

        {/* Columna resaltada al pasar por encima: blanco de ratón generoso. */}
        {encima !== null && (
          <rect
            x={MARGEN.izquierda + paso * encima}
            y={MARGEN.arriba}
            width={paso}
            height={altoUtil}
            fill="var(--tinta)"
            opacity="0.04"
          />
        )}

        {buckets.map((b, i) => {
          const cx = xCentro(i);
          const entra = b.supply;
          const propuesto = b.planned;
          return (
            <g key={b.from}>
              {/* Lo que sale, hacia abajo. */}
              {b.demand > 0 && (
                <path d={barra(cx - anchoBarra - 1, cero, anchoBarra, y(-b.demand) - cero)} fill="var(--serie-2)" />
              )}
              {/* Lo que entra y ya existe. */}
              {entra > 0 && <path d={barra(cx + 1, y(entra), anchoBarra, cero - y(entra))} fill="var(--serie-3)" />}
              {/* Lo que entraría si se ejecuta el plan: mismo sitio, con trama. */}
              {propuesto > 0 && (
                <path
                  d={barra(cx + 1, y(entra + propuesto), anchoBarra, y(entra) - y(entra + propuesto))}
                  fill={`url(#trama-${id})`}
                  stroke="var(--serie-3)"
                  strokeWidth="1"
                />
              )}
            </g>
          );
        })}

        {/* Cómo quedaría sin hacer nada. Mismo tono, discontinua: es la misma
            magnitud en otro escenario, no otra cosa. */}
        <path d={lineaSinPlan} fill="none" stroke="var(--serie-1)" strokeWidth="2" strokeDasharray="4 4" opacity="0.45" />
        {/* Cómo queda con el plan. */}
        <path d={lineaProyectado} fill="none" stroke="var(--serie-1)" strokeWidth="2" strokeLinejoin="round" />

        {encima !== null && buckets[encima] && (
          <circle cx={xCentro(encima)} cy={y(buckets[encima].projected)} r="5" fill="var(--serie-1)" stroke="var(--superficie)" strokeWidth="2" />
        )}

        {/* Etiqueta directa del último valor: el dato que más se mira, sin
            obligar a cruzar la vista hasta la leyenda. */}
        {ultimo && (
          <text x={ancho - MARGEN.derecha + 6} y={y(ultimo.projected) + 4} fontSize="11" fontWeight="600" fill="var(--tinta)">
            {formatear(ultimo.projected)}
          </text>
        )}

        {buckets.map((b, i) =>
          i % Math.ceil(buckets.length / 8) === 0 ? (
            <text key={b.from} x={xCentro(i)} y={ALTO - 12} textAnchor="middle" fontSize="11" fill="var(--tinta-3)">
              {etiquetaFecha(b.from)}
            </text>
          ) : null,
        )}
      </svg>

      {encima !== null && buckets[encima] && (
        <Globo cubo={buckets[encima]} x={raton.x} y={raton.y} unidad={unidad} />
      )}

      <div className="leyenda" style={{ marginTop: 8 }}>
        <span className="item">
          <span className="muestra" style={{ background: 'var(--serie-2)' }} /> Necesidades
        </span>
        <span className="item">
          <span className="muestra" style={{ background: 'var(--serie-3)' }} /> Cobertura ya pedida
        </span>
        <span className="item">
          <span
            className="muestra"
            style={{
              background:
                'repeating-linear-gradient(45deg, var(--serie-3) 0 3px, var(--superficie) 3px 6px)',
              border: '1px solid var(--serie-3)',
            }}
          />{' '}
          Propuesto por el MRP
        </span>
        <span className="item">
          <span className="muestra" style={{ background: 'var(--serie-1)', height: 3, borderRadius: 2 }} /> Stock proyectado
        </span>
        <span className="item">
          <span
            className="muestra"
            style={{
              height: 3,
              borderRadius: 2,
              background: 'repeating-linear-gradient(90deg, var(--serie-1) 0 4px, transparent 4px 8px)',
            }}
          />{' '}
          Si no se hace nada
        </span>
      </div>
    </div>
  );
}

function Globo({ cubo, x, y, unidad }: { cubo: Cubo; x: number; y: number; unidad: string | null }): JSX.Element {
  const u = unidad ? ` ${unidad}` : '';
  return (
    <div
      className="globo"
      style={{
        left: Math.min(x + 14, (typeof window === 'undefined' ? 1200 : window.innerWidth) - 210),
        top: y + 14,
      }}
    >
      <div className="cabecera">
        {etiquetaFecha(cubo.from)} – {etiquetaFecha(cubo.to)}
      </div>
      <Fila color="var(--serie-2)" nombre="Necesidades" valor={`${formatear(cubo.demand)}${u}`} />
      <Fila color="var(--serie-3)" nombre="Cobertura" valor={`${formatear(cubo.supply)}${u}`} />
      {cubo.planned > 0 && <Fila color="var(--serie-3)" nombre="Propuesto" valor={`${formatear(cubo.planned)}${u}`} />}
      <Fila color="var(--serie-1)" nombre="Proyectado" valor={`${formatear(cubo.projected)}${u}`} />
      <Fila color="var(--tinta-3)" nombre="Sin hacer nada" valor={`${formatear(cubo.projectedBare)}${u}`} />
    </div>
  );
}

function Fila({ color, nombre, valor }: { color: string; nombre: string; valor: string }): JSX.Element {
  return (
    <div className="fila">
      <span style={{ color: 'var(--tinta-2)' }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: color, marginRight: 6 }} />
        {nombre}
      </span>
      <span className="v">{valor}</span>
    </div>
  );
}

/** Barra con el extremo del dato redondeado y el de la base a escuadra. */
function barra(x: number, yTop: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, Math.abs(h));
  if (h >= 0) {
    // Crece hacia abajo desde yTop: el extremo redondeado es el de abajo.
    return `M${x},${yTop} h${w} v${h - r} a${r},${r} 0 0 1 ${-r},${r} h${-(w - 2 * r)} a${r},${r} 0 0 1 ${-r},${-r} Z`;
  }
  return `M${x},${yTop + r} a${r},${r} 0 0 1 ${r},${-r} h${w - 2 * r} a${r},${r} 0 0 1 ${r},${r} v${-h - r} h${-w} Z`;
}

function marcasEje(min: number, max: number): number[] {
  const paso = escalonBonito((max - min) / 4);
  const inicio = Math.ceil(min / paso) * paso;
  const out: number[] = [];
  for (let v = inicio; v <= max; v += paso) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

function escalonBonito(bruto: number): number {
  const exp = Math.floor(Math.log10(Math.abs(bruto) || 1));
  const base = 10 ** exp;
  for (const m of [1, 2, 2.5, 5, 10]) if (bruto / base <= m) return m * base;
  return 10 * base;
}

export function formatear(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const decimales = abs >= 100 || Number.isInteger(n) ? 0 : abs >= 1 ? 1 : 2;
  return n.toLocaleString('es-ES', { maximumFractionDigits: decimales });
}

export function etiquetaFecha(iso: string): string {
  const d = String(iso).slice(0, 10).split('-');
  return `${d[2]}/${d[1]}`;
}
